import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiList, FiMap } from 'react-icons/fi';
import { ActionIcon, Group, Loader, Stack } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { dispatchQueryKeys } from 'util/queryKeys';
import { dispatchPlansQueryOptions } from 'util/queryOptions';
import { DispatchFilter, DispatchStateType, DispatchType } from 'store/dispatch/types';
import { Header } from 'common/Components/Mantine/Header';

import { DispatchMap } from './Map/DispatchMap';
import { DispatchTable } from './Table/DispatchTable';
import { DispatchFilters } from './DispatchFilters';

const DispatchContainer = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [mapView, toggleMapView] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<DispatchFilter>({
    dispatch_status: undefined,
    start_at: undefined,
    end_at: undefined,
    alert_name: undefined,
    country_code: undefined,
    sampler_only: false,
    snoozed_alerts_only: false,
    map_bounds: undefined,
  });

  const {
    data,
    isFetching,
  }: { data: DispatchStateType | undefined; isFetching: boolean | undefined } = useQuery(
    dispatchPlansQueryOptions(page, filter),
  );
  const dispatchRows: DispatchType[] = data?.sampling_plans.items || [];

  const refreshQuery = () => {
    queryClient.invalidateQueries({
      queryKey: dispatchQueryKeys.dispatchPlans(page, filter),
    });
  };

  useEffect(() => {
    refreshQuery();
  }, [filter, page]);

  const handleUpdateFilter = (
    key: keyof DispatchFilter,
    value: string | boolean | undefined | null,
  ) => setFilter((current) => ({ ...current, [key]: value }));

  return (
    <Stack>
      <Header
        title={
          <Group>
            {t('samplePlanTracking')}
            {isFetching && <Loader size="sm" />}
          </Group>
        }
      >
        <Group>
          <ActionIcon
            variant="subtle"
            radius="xl"
            size="xl"
            onClick={() => toggleMapView(!mapView)}
          >
            {mapView ? <FiList size="1.5rem" /> : <FiMap size="1.5rem" />}
          </ActionIcon>
        </Group>
      </Header>
      <DispatchFilters dispatch={data} filter={filter} onValueChange={handleUpdateFilter} />
      {mapView ? (
        <DispatchMap
          dispatchPlans={dispatchRows}
          refreshPage={refreshQuery}
          filter={filter}
          updateFilter={handleUpdateFilter}
        />
      ) : (
        <DispatchTable
          fetching={isFetching}
          records={dispatchRows}
          totalRecords={data?.sampling_plans.total || 0}
          recordsPerPage={data?.sampling_plans.per_page || 0}
          page={page}
          onPageChange={setPage}
          refreshPage={refreshQuery}
        />
      )}
    </Stack>
  );
};

export default DispatchContainer;
